import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../../../../constants';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { ConversionRatePerformanceItem } from '../../../../../hooks';
import { Container } from '../../../../../stories/atoms';
import { muiTheme } from '../../../../../theme';
import { homeTranslations, kpiTargetTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { WidgetMetricTile, WidgetMetricType } from '../../../common/metricTile/WidgetMetricTile';
import { WidgetMetricTilesContainer } from '../../../common/metricTile/WidgetMetricTile.styles';
import { getDefaultStatus } from '../../../common/metricTile/util';
import { StyledTrendIconComponent } from '../../../common/styledTrendIcon/StyledTrendIcon';
import WidgetChart from '../../../common/widgetChart/WidgetChart';
import { WidgetMobileLayout } from '../../../common/widgetMobileLayout/WidgetMobileLayout';

export const ConversionRateGraphView = ({
  target,
  today,
  lastWeek,
  lastYear,
  interval,
  performanceItems,
  filteredPerformanceItems,
  forecastPerformanceItems,
  targetItems,
  isKpiLoading,
  isKpiError,
  isToday,
}: {
  target: number | null;
  today: number;
  lastWeek: number;
  lastYear: number;
  interval: StorePerformanceInterval;
  performanceItems: ConversionRatePerformanceItem[];
  filteredPerformanceItems: (ConversionRatePerformanceItem | null)[];
  forecastPerformanceItems: (ConversionRatePerformanceItem | null)[];
  targetItems: (number | null)[] | undefined;
  isKpiLoading: boolean;
  isKpiError: boolean;
  isToday: boolean;
}) => {
  const { conversionRate: conversionRateTranslation, common: commonTranslations } = homeTranslations;
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const { mode } = useThemeContext();
  const { t } = useTranslation();

  const changeNote = isToday
    ? ''
    : t(commonTranslations.changeVsCurrentNote.key, commonTranslations.changeVsCurrentNote.defaultValue);

  const itemValueResolver = (item: ConversionRatePerformanceItem) => {
    const { payingCustomers, nonPayingCustomers } = item;
    if (nonPayingCustomers === 0 && payingCustomers === 0) {
      return 0;
    }
    return Number.parseFloat(((payingCustomers / (nonPayingCustomers + payingCustomers)) * 100).toFixed(2));
  };

  const getConversionRate = (items: (ConversionRatePerformanceItem | null)[]) => {
    let hoursElapsed = 0;
    let payingCustomersCount = 0;
    let nonPayingCustomersCount = 0;
    let numberOfTillsCount = 0;

    items.forEach((item) => {
      if (item === null) return;
      hoursElapsed += 1;
      payingCustomersCount += item.payingCustomers;
      nonPayingCustomersCount += item.nonPayingCustomers;
      numberOfTillsCount += item.numberOfTills;
    });

    const averagePayingCustomers = payingCustomersCount / hoursElapsed;
    const averageNonPayingCustomers = nonPayingCustomersCount / hoursElapsed;
    const averageAmountOfTills = numberOfTillsCount / hoursElapsed;

    const averageTillObject = {
      hour: 'Average',
      payingCustomers: averagePayingCustomers,
      nonPayingCustomers: averageNonPayingCustomers,
      numberOfTills: Math.floor(averageAmountOfTills),
    };

    const conversionRate =
      payingCustomersCount === 0 && nonPayingCustomersCount === 0
        ? 0
        : (payingCustomersCount / (payingCustomersCount + nonPayingCustomersCount)) * 100;

    return {
      conversionRate,
      averageTillObject,
      averagePayingCustomers,
      averageNonPayingCustomers,
    };
  };

  const { conversionRate: currentConversionRate } = getConversionRate(filteredPerformanceItems);

  const { conversionRate: forecastConversionRate } = getConversionRate([
    ...filteredPerformanceItems,
    ...forecastPerformanceItems,
  ]);

  const targetProgressPercent = target && target != 0 ? (forecastConversionRate / target) * 100 : 0;

  const showForecast = !!target && forecastPerformanceItems.some((item) => item !== null);

  return (
    <>
      {!isMobile && (
        <>
          <WidgetMetricTilesContainer>
            {showForecast && (
              <WidgetMetricTile
                metricProperties={{
                  type: WidgetMetricType.ForecastMetric,
                  properties: {
                    metricValue: formatAmountWithSeparator(forecastConversionRate, '%'),
                    forecastIndicator: getDefaultStatus(targetProgressPercent),
                    metricTooltipText: t(
                      commonTranslations.forecastTileTooltip.key,
                      commonTranslations.forecastTileTooltip.defaultValue,
                    ),
                  },
                }}
              />
            )}

            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.LegendMetric,
                properties: {
                  metricTitle: t(
                    homeTranslations.conversionRate.title.key,
                    homeTranslations.conversionRate.title.defaultValue,
                  ),
                  metricValue: formatAmountWithSeparator(currentConversionRate, '%'),
                  legendColor: 'validationGreen',
                  metricTooltipText: t(
                    conversionRateTranslation.metricTileTooltip.conversionRateTile.key,
                    conversionRateTranslation.metricTileTooltip.conversionRateTile.defaultValue,
                  ),
                },
              }}
            />

            {target !== null && (
              <WidgetMetricTile
                isLoading={isKpiLoading}
                isError={isKpiError}
                metricProperties={{
                  type: WidgetMetricType.LegendMetric,
                  properties: {
                    metricTitle: t(
                      kpiTargetTranslations.kpiProperties.target.title.key,
                      kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                    ),
                    metricValue: formatAmountWithSeparator(target, '%'),
                    legendColor: 'tagBlue',
                    metricTooltipText: t(
                      conversionRateTranslation.metricTileTooltip.targetTile.key,
                      conversionRateTranslation.metricTileTooltip.targetTile.defaultValue,
                    ),
                  },
                }}
              />
            )}

            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.RegularMetric,
                properties: {
                  metricTitle: t(commonTranslations.lastWeek.key, commonTranslations.lastWeek.defaultValue),
                  metricValue: (
                    <StyledTrendIconComponent
                      originalAmount={today}
                      previousAmount={lastWeek}
                      direction="horizontal"
                      valueFormatter={(value) => formatAmountWithSeparator(value, '%')}
                      textOptions={{ type: 'caption' }}
                      hasPercentage={false}
                      hideTrendText={isToday}
                    />
                  ),
                  metricTooltipText: t(
                    conversionRateTranslation.metricTileTooltip.lastWeekTile.key,
                    conversionRateTranslation.metricTileTooltip.lastWeekTile.defaultValue,
                    { changeNote },
                  ),
                },
              }}
            />

            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.RegularMetric,
                properties: {
                  metricTitle: t(commonTranslations.lastYear.key, commonTranslations.lastYear.defaultValue),
                  metricValue: (
                    <StyledTrendIconComponent
                      originalAmount={today}
                      previousAmount={lastYear}
                      direction="horizontal"
                      valueFormatter={(value) => formatAmountWithSeparator(value, '%')}
                      textOptions={{ type: 'caption' }}
                      hasPercentage={false}
                      hideTrendText={isToday}
                    />
                  ),
                  metricTooltipText: t(
                    conversionRateTranslation.metricTileTooltip.lastYearTile.key,
                    conversionRateTranslation.metricTileTooltip.lastYearTile.defaultValue,
                    { changeNote },
                  ),
                },
              }}
            />
          </WidgetMetricTilesContainer>

          <Container wrap="nowrap" data-testid="chart" style={{ flex: 1 }}>
            <WidgetChart
              type="bar"
              performanceItemsChartName={t(
                conversionRateTranslation.title.key,
                conversionRateTranslation.title.defaultValue,
              )}
              performanceItems={performanceItems}
              filteredPerformanceItems={filteredPerformanceItems}
              forecastPerformanceItems={forecastPerformanceItems}
              targetItems={targetItems}
              itemValueResolver={itemValueResolver}
              showXAxis={true}
              yAxisFormatter={(value) => formatAmountWithSeparator(Math.floor(value), '%')}
              tooltipFormatter={(value) => formatAmountWithSeparator(value, '%')}
              interval={interval}
              dataLabel={{
                enabled: true,
                enabledOnSeries: [0, 2],
                offsetY: -28,
                style: {
                  fontSize: isMobile ? '8px' : '12px',
                  fontWeight: '100',
                  colors: [mode === 'dark' ? muiTheme.color.white : muiTheme.color.primary],
                },
                formatter: function (value) {
                  return value ? value + '%' : '';
                },
              }}
            />
          </Container>
        </>
      )}
      {isMobile && (
        <Container padding={[2, 6]} wrap="nowrap">
          {showForecast && (
            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.ForecastMetric,
                properties: {
                  metricValue: formatAmountWithSeparator(forecastConversionRate, '%'),
                  forecastIndicator: getDefaultStatus(targetProgressPercent),
                  metricTooltipText: t(
                    commonTranslations.forecastTileTooltip.key,
                    commonTranslations.forecastTileTooltip.defaultValue,
                  ),
                },
              }}
            />
          )}

          <WidgetMobileLayout
            metricItems={[
              {
                label: t(conversionRateTranslation.title.key, conversionRateTranslation.title.defaultValue),
                content: formatAmountWithSeparator(today, '%'),
                tooltipText: t(
                  conversionRateTranslation.metricTileTooltip.conversionRateTile.key,
                  conversionRateTranslation.metricTileTooltip.conversionRateTile.defaultValue,
                ),
              },

              {
                label: t(
                  kpiTargetTranslations.kpiProperties.target.title.key,
                  kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                ),
                content: target === null ? `-` : formatAmountWithSeparator(target, '%'),
                isLoading: isKpiLoading,
                isError: isKpiError,
                tooltipText: t(
                  conversionRateTranslation.metricTileTooltip.targetTile.key,
                  conversionRateTranslation.metricTileTooltip.targetTile.defaultValue,
                ),
              },

              {
                label: t(commonTranslations.lastWeek.key, commonTranslations.lastWeek.defaultValue),
                content: (
                  <StyledTrendIconComponent
                    originalAmount={today}
                    previousAmount={lastWeek}
                    direction="horizontal"
                    valueFormatter={(value) => formatAmountWithSeparator(value, '%')}
                    textOptions={{ type: 'caption' }}
                    hasPercentage={false}
                    hideTrendText={isToday}
                  />
                ),
                tooltipText: t(
                  conversionRateTranslation.metricTileTooltip.lastWeekTile.key,
                  conversionRateTranslation.metricTileTooltip.lastWeekTile.defaultValue,
                  { changeNote },
                ),
              },
              {
                label: t(commonTranslations.lastYear.key, commonTranslations.lastYear.defaultValue),
                content: (
                  <StyledTrendIconComponent
                    originalAmount={today}
                    previousAmount={lastYear}
                    direction="horizontal"
                    valueFormatter={(value) => formatAmountWithSeparator(value, '%')}
                    textOptions={{ type: 'caption' }}
                    hasPercentage={false}
                    hideTrendText={isToday}
                  />
                ),
                tooltipText: t(
                  conversionRateTranslation.metricTileTooltip.lastYearTile.key,
                  conversionRateTranslation.metricTileTooltip.lastYearTile.defaultValue,
                  { changeNote },
                ),
              },
            ]}
            renderChart={() => {
              return (
                <WidgetChart
                  type="bar"
                  performanceItemsChartName={t(
                    conversionRateTranslation.title.key,
                    conversionRateTranslation.title.defaultValue,
                  )}
                  performanceItems={performanceItems}
                  filteredPerformanceItems={filteredPerformanceItems}
                  forecastPerformanceItems={forecastPerformanceItems}
                  targetItems={targetItems}
                  interval={interval}
                  itemValueResolver={itemValueResolver}
                  showXAxis={true}
                  showXAxisTooltip
                  yAxisFormatter={(value) => `${Math.floor(value)}%`}
                  tooltipFormatter={(value) => formatAmountWithSeparator(value, '%')}
                  dataLabel={{
                    enabled: false,
                  }}
                  sparklineEnabled
                  customYAxis={{ show: false }}
                />
              );
            }}
          />
        </Container>
      )}
    </>
  );
};
